/* ============================================================
   Floating Header — behavior (v3, unchanged in this fix)
   - The whole header (announcement bar + nav bar) is
     position:sticky as one unit — it naturally sits below
     whatever comes before it in the page (e.g. a marquee
     section) and only pins once it reaches the top, so it can
     never cover something above it. Scroll distance drives
     purely cosmetic changes: the announcement bar collapsing,
     and the nav bar's padding/shadow.
   - Desktop dropdowns: open with a dimming overlay, close on
     outside click, overlay click, link click, or Escape
   - Desktop sub-dropdowns: "Dropdown link" items with their own
     "Dropdown sub-item" blocks expand inline as an image grid
   - Mobile: hamburger <-> X animation, sliding drawer, and
     accordion-style sub-menus (including nested sub-accordions)
   - Cart count: renders from Liquid on load, and updates live
     via a `cart:updated` custom event (dispatch this from your
     add-to-cart / AJAX cart code with
     detail: { itemCount: <number> })
   - Re-initializes automatically when the Shopify theme editor
     re-renders this section (shopify:section:load)

   NOTE: the mobile drawer overlapping the header pill (reported
   on mobile) was a pure CSS stacking (z-index) issue — fixed
   entirely in header.css. No JS changes were needed for that.
   NOTE (v6): the cart pill's dedicated background/count-color
   settings are pure CSS too (header.css + the CSS variables set
   in header.liquid) — nothing here needed to change for that
   either.
   ============================================================ */

function initAllHeaders(root) {
  var scope = root || document;
  scope.querySelectorAll('[data-header-wrapper]').forEach(function (wrapper) {
    if (wrapper.dataset.headerInitialized === 'true') return;
    wrapper.dataset.headerInitialized = 'true';
    initHeader(wrapper);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', function () {
    initAllHeaders();
  });
} else {
  initAllHeaders();
}

document.addEventListener('shopify:section:load', function (event) {
  initAllHeaders(event.target);
});

function toggleAccordion(acc, trigger, panel, siblingsScope, siblingSelector) {
  var isOpen = acc.classList.contains('is-open');

  if (siblingsScope && siblingSelector) {
    siblingsScope.querySelectorAll(siblingSelector).forEach(function (other) {
      if (other !== acc && other.parentElement === acc.parentElement) {
        other.classList.remove('is-open');
        var otherPanel = other.querySelector(':scope > [data-accordion-panel], :scope > [data-sub-panel]');
        var otherTrigger = other.querySelector(':scope > [data-accordion-trigger], :scope > [data-sub-trigger]');
        if (otherPanel) otherPanel.style.maxHeight = null;
        if (otherTrigger) otherTrigger.setAttribute('aria-expanded', 'false');
      }
    });
  }

  if (isOpen) {
    acc.classList.remove('is-open');
    panel.style.maxHeight = null;
    trigger.setAttribute('aria-expanded', 'false');
  } else {
    acc.classList.add('is-open');
    panel.style.maxHeight = panel.scrollHeight + 'px';
    trigger.setAttribute('aria-expanded', 'true');
  }
}

function initHeader(wrapper) {
  var root = wrapper.parentElement || document;
  var header = wrapper.querySelector('[data-site-header]');
  var spacer = root.querySelector('[data-header-spacer]');
  var overlay = root.querySelector('[data-header-overlay]');
  var hamburger = root.querySelector('[data-hamburger]');
  var drawer = root.querySelector('[data-mobile-drawer]');
  var drawerOverlay = root.querySelector('[data-drawer-overlay]');
  var drawerClose = root.querySelector('[data-drawer-close]');
  var threshold = parseInt(wrapper.getAttribute('data-scroll-threshold'), 10) || 80;
  var ticking = false;

  /* ---------- Manual sticky (position:fixed only once scrolled past
     the wrapper's natural position) ---------- */
  var naturalTop = 0;
  var naturalHeight = 0;

  function measureNaturalPosition() {
    // Only re-measure while NOT pinned, so we're always measuring the
    // wrapper's true position in normal flow, not its pinned position.
    if (wrapper.classList.contains('is-pinned')) return;
    var rect = wrapper.getBoundingClientRect();
    naturalTop = rect.top + window.scrollY;
    naturalHeight = rect.height;
  }

  measureNaturalPosition();

  function updateScrollState() {
    var scrolled = window.scrollY > naturalTop + threshold;
    var wasPinned = wrapper.classList.contains('is-pinned');

    if (scrolled && !wasPinned) {
      if (spacer) spacer.style.height = naturalHeight + 'px';
      wrapper.classList.add('is-pinned');
      wrapper.classList.add('is-scrolled');
    } else if (!scrolled && wasPinned) {
      wrapper.classList.remove('is-pinned');
      wrapper.classList.remove('is-scrolled');
      if (spacer) spacer.style.height = '0px';
      // Re-measure now that we're back in normal flow, in case
      // anything above the header (e.g. the marquee) changed height.
      measureNaturalPosition();
    }

    ticking = false;
  }

  window.addEventListener('resize', function () {
    measureNaturalPosition();
    updateScrollState();
  });

  window.addEventListener(
    'scroll',
    function () {
      if (!ticking) {
        window.requestAnimationFrame(updateScrollState);
        ticking = true;
      }
    },
    { passive: true }
  );
  updateScrollState();

  /* ---------- Desktop dropdowns (top-level: Shop, etc.) ---------- */
  var dropdowns = wrapper.querySelectorAll('[data-nav-dropdown]');

  function closeAllDropdowns() {
    dropdowns.forEach(function (dd) {
      dd.classList.remove('is-open');
      var trigger = dd.querySelector('[data-dropdown-trigger]');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      // Also collapse any open sub-accordions inside it
      dd.querySelectorAll('[data-sub-accordion].is-open').forEach(function (sub) {
        sub.classList.remove('is-open');
        var subPanel = sub.querySelector('[data-sub-panel]');
        var subTrigger = sub.querySelector('[data-sub-trigger]');
        if (subPanel) subPanel.style.maxHeight = null;
        if (subTrigger) subTrigger.setAttribute('aria-expanded', 'false');
      });
    });
    if (overlay) overlay.classList.remove('is-active');
  }

  dropdowns.forEach(function (dd) {
    var trigger = dd.querySelector('[data-dropdown-trigger]');
    if (!trigger) return;

    trigger.addEventListener('click', function (e) {
      e.stopPropagation();
      var isOpen = dd.classList.contains('is-open');
      closeAllDropdowns();
      if (!isOpen) {
        dd.classList.add('is-open');
        trigger.setAttribute('aria-expanded', 'true');
        if (overlay) overlay.classList.add('is-active');
      }
    });

    /* ---------- Desktop sub-dropdowns (e.g. All Flavors within Shop) ---------- */
    dd.querySelectorAll('[data-sub-accordion]').forEach(function (sub) {
      var subTrigger = sub.querySelector(':scope > [data-sub-trigger]');
      var subPanel = sub.querySelector(':scope > [data-sub-panel]');
      if (!subTrigger || !subPanel) return;

      subTrigger.addEventListener('click', function (e) {
        e.stopPropagation();
        toggleAccordion(sub, subTrigger, subPanel, sub.parentElement, ':scope > [data-sub-accordion]');
      });
    });
  });

  if (overlay) {
    overlay.addEventListener('click', closeAllDropdowns);
  }

  document.addEventListener('click', function (e) {
    if (!wrapper.contains(e.target)) closeAllDropdowns();
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeAllDropdowns();
  });

  /* ---------- Mobile drawer ---------- */
  /*
     FIX (marquee section showing through/above the open drawer):
     a marquee/ticker section elsewhere on the page can be built with
     its own position: fixed/sticky and z-index high enough to sit
     above the drawer no matter how high the drawer's own z-index is
     pushed (header.css already raises the drawer to the maximum
     possible z-index as a first line of defense). To guarantee the
     marquee is genuinely not visible while the drawer is open — not
     just covered by it — every element on the page whose section
     wrapper id or class name contains "marquee" (case-insensitive,
     so this matches whatever the marquee section happens to be
     named) is explicitly hidden the moment the drawer opens, and
     restored to exactly how it was the moment the drawer closes.
  */
  function setMarqueeHidden(hidden) {
    var marqueeEls = document.querySelectorAll(
      '[class*="marquee" i], [id*="marquee" i]'
    );
    marqueeEls.forEach(function (el) {
      if (hidden) {
        if (!el.hasAttribute('data-header-prev-visibility')) {
          el.setAttribute('data-header-prev-visibility', el.style.visibility || '');
        }
        el.style.visibility = 'hidden';
      } else if (el.hasAttribute('data-header-prev-visibility')) {
        el.style.visibility = el.getAttribute('data-header-prev-visibility');
        el.removeAttribute('data-header-prev-visibility');
      }
    });
  }

  function openDrawer() {
    if (!drawer) return;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    if (drawerOverlay) drawerOverlay.classList.add('is-active');
    if (hamburger) {
      hamburger.classList.add('is-open');
      hamburger.setAttribute('aria-expanded', 'true');
    }
    document.documentElement.style.overflow = 'hidden';
    setMarqueeHidden(true);
  }

  function closeDrawer() {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    if (drawerOverlay) drawerOverlay.classList.remove('is-active');
    if (hamburger) {
      hamburger.classList.remove('is-open');
      hamburger.setAttribute('aria-expanded', 'false');
    }
    document.documentElement.style.overflow = '';
    setMarqueeHidden(false);
  }

  if (hamburger) {
    hamburger.addEventListener('click', function () {
      if (drawer && drawer.classList.contains('is-open')) {
        closeDrawer();
      } else {
        openDrawer();
      }
    });
  }

  if (drawerClose) drawerClose.addEventListener('click', closeDrawer);
  if (drawerOverlay) drawerOverlay.addEventListener('click', closeDrawer);

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeDrawer();
  });

  /* ---------- Mobile accordions (top-level AND nested sub-accordions) ---------- */
  if (drawer) {
    drawer.querySelectorAll('[data-mobile-accordion]').forEach(function (acc) {
      var trigger = acc.querySelector(':scope > [data-accordion-trigger]');
      var panel = acc.querySelector(':scope > [data-accordion-panel]');
      if (!trigger || !panel) return;

      trigger.addEventListener('click', function (e) {
        e.stopPropagation();
        toggleAccordion(acc, trigger, panel, acc.parentElement, ':scope > [data-mobile-accordion]');
      });
    });
  }

  /* ---------- Cart count ---------- */
  var cartLinks = document.querySelectorAll('[data-cart-count]');

  function setCartCount(count) {
    cartLinks.forEach(function (el) {
      el.textContent = count;
      el.hidden = count <= 0;
      var cartIcon = el.closest('[data-cart-icon]');
      if (cartIcon) {
        cartIcon.classList.add('is-bumping');
        window.setTimeout(function () {
          cartIcon.classList.remove('is-bumping');
        }, 220);
      }
    });
  }

  document.addEventListener('cart:updated', function (e) {
    if (e.detail && typeof e.detail.itemCount === 'number') {
      setCartCount(e.detail.itemCount);
    }
  });

  window.updateHeaderCartCount = setCartCount;
}